import React, { FC, useState } from 'react'
import { Modal, Button, Row, Table, Spin } from 'antd'
import { isEmpty } from 'lodash'
import { ModalProps } from 'antd/lib/modal'
import { useTranslation } from 'react-i18next'
import { useDispatch, connect, useSelector } from 'react-redux'
import { change, getFormSubmitErrors, initialize, isValid, SubmissionError, submit } from 'redux-form'

// assets
import { ReactComponent as CloseIcon } from '../../assets/icons/BaseIcons/x-icon.svg'

// forms
import CodeEditorForm from '../forms/CodeEditorForm'

// utils
import { postReq } from '../../utils/request'

// enums
import { FORM, DEFAULT_ERROR_MESSAGE, ELEMENT_ATTRIBUTES, SQL_EDITOR_VARIABLE_ERROR } from '../../utils/enums'

// interfaces
import { ISqlEditData } from '../../types/interfaces'
import { RootState } from '../../reducers'

// helpers
import { extract, validateVariablesInQuery } from '../../utils/helper'

type Props = ModalProps & {
	isVisible: boolean
	onCancel: any
	disabled?: boolean
	typeOfElementForm: any
	formError: any
	valid: any
	saveQueryToFields?: Function
	fieldIndex?: number
}

const SQLEditorModal: FC<Props> = (props) => {
	const { isVisible, onCancel, disabled, typeOfElementForm, formError, valid, saveQueryToFields } = props
	const { t } = useTranslation()

	const [processing, setProcessing] = useState<boolean>(false)
	const [res, setRes] = useState<any>()
	const dispatch = useDispatch()
	const sqlFormData = useSelector((state: RootState) => state.form?.[FORM.SQL_EDITOR_FORM]?.values)
	const columns = useSelector((state: RootState) => state.formTemplate.columns)
	const id = useSelector((state: RootState) => state.formTemplate.formTemplate.data?.id)

	const handleShowResult = () => {
		dispatch(submit(FORM.SQL_EDITOR_FORM))
	}

	// save query to form item general
	const saveQuery = async () => {
		const variables: string[] = extract('{', '}', sqlFormData?.[ELEMENT_ATTRIBUTES.SQL])
		// validate if query variables match columns in table
		const diff: string[] = validateVariablesInQuery(columns.data || [], variables)
		if (!(variables.length > 0 && diff.length > 0)) {
			setRes(null)
			// check if is modal opened from validation modal
			if (typeOfElementForm === FORM.VALIDATION_FORM && saveQueryToFields) {
				saveQueryToFields({
					// parse sql modal data for validation form field
					data: {
						[ELEMENT_ATTRIBUTES.SQL]: sqlFormData?.[ELEMENT_ATTRIBUTES.SQL],
						[ELEMENT_ATTRIBUTES.LINKED_FIELDS]: sqlFormData?.[ELEMENT_ATTRIBUTES.LINKED_FIELDS],
						[ELEMENT_ATTRIBUTES.SQL_VARIABLES]: variables
					},
					// add index for update field in form fields array => init from validation form "open modal button"
					index: sqlFormData?.index
				})
			} else {
				// update values
				await Promise.all([
					dispatch(change(typeOfElementForm, ELEMENT_ATTRIBUTES.SQL, sqlFormData?.[ELEMENT_ATTRIBUTES.SQL])),
					dispatch(change(typeOfElementForm, ELEMENT_ATTRIBUTES.LINKED_FIELDS, sqlFormData?.[ELEMENT_ATTRIBUTES.LINKED_FIELDS])),
					dispatch(change(typeOfElementForm, ELEMENT_ATTRIBUTES.SQL_VARIABLES, variables))
				])
			}
			// submit only if is not validation form
			if (typeOfElementForm !== FORM.VALIDATION_FORM) {
				dispatch(submit(typeOfElementForm))
			}
			onCancel()
		}
	}

	// confirm sql query
	const handleFormSubmit = async (data: ISqlEditData) => {
		try {
			if (data?.[ELEMENT_ATTRIBUTES.SQL] && data?.[ELEMENT_ATTRIBUTES.SQL] !== '' && id) {
				// check if is variables in query
				if (extract('{', '}', data?.[ELEMENT_ATTRIBUTES.SQL]).length === 0) {
					setProcessing(true)
					const query = { query: data?.[ELEMENT_ATTRIBUTES.SQL] }
					const response = await postReq(`/api/admin/forms/${id}/query`, {}, query)
					if (response?.data?.data?.type !== DEFAULT_ERROR_MESSAGE) {
						response?.data?.data?.result?.map((item: any, index: number) => {
							return { ...item, key: index }
						})
						setRes(response?.data)
					} else {
						const sqlError = {
							name: response?.data?.data.result.name,
							message: response?.data?.data.result.original.message
						}
						throw new SubmissionError(sqlError)
					}
				} else {
					throw new SubmissionError({
						name: t(`loc:${SQL_EDITOR_VARIABLE_ERROR}`),
						message: t('loc:If you are using variables in query, show result function is disabled! For check result go to "preview" tab.')
					})
				}
			}
		} catch (err: any) {
			// eslint-disable-next-line no-console
			console.error(err.message)
			throw err
		} finally {
			setProcessing(false)
		}
	}

	const parseDataToTableColumns = (data: any[]): any[] => {
		const tableColumns: any[] = []
		if (data && !isEmpty(data)) {
			Object.keys(data[0]).forEach((value: any) => {
				tableColumns.push({
					title: value,
					dataIndex: value
				})
			})
		}
		return tableColumns.filter((col) => col.dataIndex !== 'key')
	}

	return (
		<Modal
			closeIcon={<CloseIcon width={18} height={18} />}
			title={t('loc:SQL editor')}
			centered
			open={isVisible}
			width={840}
			onCancel={() => {
				setRes(null)
				dispatch(initialize(FORM.SQL_EDITOR_FORM, {}))
				onCancel()
			}}
			footer={null}
		>
			<Row justify={'center'} className={'w-full'}>
				<div className={'flex w-full'}>
					<CodeEditorForm onSubmit={handleFormSubmit} columns={columns} />
					<ul className={'w-3/12 h-96 overflow-auto'}>{columns.data?.map((column: any, index) => <li key={index}>{column?.label}</li>)}</ul>
				</div>
				<Button disabled={false} className={'h-10 w-full unfilled-btn'} key='show' onClick={handleShowResult}>
					{t('loc:Show result')}
				</Button>
				{valid &&
					(res?.data?.result || (formError && formError?.name)) &&
					(processing === true ? (
						<Spin className={'w-full mt-20'} />
					) : (
						<div className={'w-full mt-6 scollable scrollable--smaller'}>
							<span className={'block mb-2'}>{t('loc:Result')}</span>
							<div className={'w-full'}>
								{formError && formError?.name ? (
									<div>
										<div>{formError.name}</div>
										<div>{formError.message}</div>
									</div>
								) : (
									<Table columns={parseDataToTableColumns(res?.data?.result)} dataSource={res?.data?.result} />
								)}
							</div>
						</div>
					))}
			</Row>
			<Row justify={'end'} className={'w-full mt-6'}>
				<Button
					disabled={disabled || false}
					style={{ width: 124, height: 40 }}
					key='back'
					type='text'
					onClick={() => {
						setRes(null)
						dispatch(initialize(FORM.SQL_EDITOR_FORM, {}))
						onCancel()
					}}
				>
					{t('loc:Close')}
				</Button>
				<Button
					className={'h-10 ant-btn-primary'}
					key='confirm'
					disabled={disabled || (formError?.name && formError?.name !== t(`loc:${SQL_EDITOR_VARIABLE_ERROR}`))}
					onClick={() => {
						saveQuery()
					}}
				>
					{t('loc:Confirm')}
				</Button>
			</Row>
		</Modal>
	)
}

const mapFormErrorsToProps = (state: any) => ({
	formError: getFormSubmitErrors(FORM.SQL_EDITOR_FORM)(state),
	valid: isValid(FORM.SQL_EDITOR_FORM)(state)
})

export default connect(mapFormErrorsToProps)(SQLEditorModal)
